import { DataProvider, HttpError } from "@refinedev/core";
import { axiosInstance, generateSort, generateFilter } from "functions";
import { AxiosInstance } from "axios";
import { stringify } from "query-string";
import { IParticipation } from "../interfaces";

type MethodTypes = "get" | "delete" | "head" | "options";
type MethodTypesWithBody = "post" | "put" | "patch";

export const participationDataProvider = (
  apiUrl: string,
  httpClient: AxiosInstance = axiosInstance
): Omit<
  Required<DataProvider>,
  "createMany" | "updateMany" | "deleteMany"
> => ({
  getList: async ({ resource, pagination, filters, sorters, meta }) => {
  
    var url = `${apiUrl}/${resource}`;

    const { current = 1, pageSize = 10, mode = "server" } = pagination ?? {};

    const { headers: headersFromMeta, method } = meta ?? {};
    const requestMethod = (method as MethodTypes) ?? "get";

    const query: {
      page?: number;
      size?: number;
    } = {};

    if (mode === "server") {
      query.page = current - 1;
      query.size = pageSize;
    }

    const combinedQuery = { sorters: sorters, filters: filters, ...query };

    const urlWithQuery = `${url}/q?params=${encodeURIComponent(JSON.stringify(combinedQuery))}`;
    // console.log("urlWithQuery", urlWithQuery);

    var { data, headers } = await httpClient[requestMethod](urlWithQuery, {
      headers: headersFromMeta,
    });

    const total = +data.totalElements;
    data = data.content;

    return {
      data,
      total: total || data.length,
    };
  },

  getMany: async ({ resource, ids, meta }) => {
    const { headers, method } = meta ?? {};
    const requestMethod = (method as MethodTypes) ?? "get";

    const { data } = await httpClient[requestMethod](
      `${apiUrl}/${resource}?${stringify({ id: ids })}`,
      { headers }
    );

    return {
      data,
    };
  },

  create: async ({ resource, variables, meta }) => { console.log("create", resource, variables, meta);
  const url = `${apiUrl}/${resource}`;

  const { headers, method } = meta ?? {};
  const requestMethod = (method as MethodTypesWithBody) ?? "post";

  const { data } = await httpClient[requestMethod](url, variables, {
    headers,
  });

    return {
      data,
    };
  },

  update: async ({ resource, id, variables, meta }) => { console.log("update", resource, id, variables, meta);
    const url = `${apiUrl}/${resource}/${id}`;

    const { headers, method } = meta ?? {};
    const requestMethod = (method as MethodTypesWithBody) ?? "patch";

    const { data } = await httpClient[requestMethod](url, variables, {
      headers,
    });

    return {
      data,
    };
  },

  getOne: async ({ resource, id, meta }) => { console.log("getOne", resource, id, meta);
    // GET /{resource}/{id}는 Spring HATEOAS 단건 표현이라 createdDate/updatedDate/_links 뿐이고
    // FK 스칼라 필드가 없다(2026-09 확인, 백엔드 이슈 #69). 목록이 쓰는 검색 엔드포인트(/{resource}/q)는
    // id 필터를 지원하고 평탄화된 실제 필드를 주므로 단건 조회도 이 경로로 통일한다.
    // sorters는 빈 배열이라도 반드시 넘겨야 한다 — 백엔드가 sorters를 null로 받으면
    // NPE(sorters.isEmpty())로 400을 반환한다(getList와 동일하게 맞춰야 함).
    const query = { sorters: [], filters: [{ field: "id", operator: "eq", value: id }], page: 0, size: 1 };
    const url = `${apiUrl}/${resource}/q?params=${encodeURIComponent(JSON.stringify(query))}`;

    const { headers, method } = meta ?? {};
    const requestMethod = (method as MethodTypes) ?? "get";

    const { data } = await httpClient[requestMethod](url, { headers });
    const record = data?.content?.[0];

    if (!record) {
      throw new Error(`${resource} id=${id} 레코드를 찾을 수 없습니다.`);
    }

    return {
      data: record,
    };
  },

  deleteOne: async ({ resource, id, variables, meta }) => { console.log("deleteOne", resource, id, variables, meta);
    const url = `${apiUrl}/${resource}/${id}`;

    const { headers, method } = meta ?? {};
    const requestMethod = (method as MethodTypesWithBody) ?? "delete";

    const { data } = await httpClient[requestMethod](url, {
      data: variables,
      headers,
    });

    return {
      data,
    };
  },

  getApiUrl: () => {
    return apiUrl;
  },

  custom: async ({
    url,
    method,
    filters,
    sorters,
    payload,
    query,
    headers,
  }) => {
    let requestUrl = `${url}?`;

    if (sorters) {
      const generatedSort = generateSort(sorters);
      if (generatedSort) {
        const { _sort, _order } = generatedSort;
        const sortQuery = {
          _sort: _sort.join(","),
          _order: _order.join(","),
        };
        requestUrl = `${requestUrl}&${stringify(sortQuery)}`;
      }
    }

    if (filters) {
      const filterQuery = generateFilter(filters);
      requestUrl = `${requestUrl}&${stringify(filterQuery)}`;
    }

    if (query) {
      requestUrl = `${requestUrl}&${stringify(query)}`;
    }

    let axiosResponse;
    switch (method) {
      case "put":
      case "post":
      case "patch":
        axiosResponse = await httpClient[method](url, payload, {
          headers,
        });
        break;
      case "delete":
        axiosResponse = await httpClient.delete(url, {
          data: payload,
          headers: headers,
        });
        break;
      default:
        axiosResponse = await httpClient.get(requestUrl, {
          headers,
        });
        break;
    }

    const { data } = axiosResponse;

    return Promise.resolve({ data });
  },
});
